// src/components/Responsabile/ResponsabileTimekeeping.jsx - Versione con gestione posizione
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { auth } from '../../firebase';
import timekeepingService from '../../services/timekeepingService';
import Notification from '../Notification';
import LocationSelectionModal from '../LocationSelectionModal';
import { getLocationName } from '../../utils/locationUtils';
import './ResponsabileTimekeeping.css';

const ResponsabileTimekeeping = () => {
  // Stati per la gestione del componente
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [scanType, setScanType] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: '', size: 'normal' });
  const [isCaptured, setIsCaptured] = useState(false);
  
  // ✅ NUOVI STATI per gestione posizione
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [pendingTimbratura, setPendingTimbratura] = useState(null);
  
  // Refs
  const html5QrCode = useRef(null);
  const timeoutRef = useRef(null);
  
  // Stati per responsabile
  const [deviceId] = useState('responsabile-scanner');
  const isAdmin = true;

  // Funzione per mostrare notifiche migliorate
  const showNotification = useCallback((message, type = 'info', customSize = null) => {
    // Determina la dimensione in base al tipo
    let size = customSize || 'normal';
    
    // Per i responsabili, usa dimensioni grandi per timbrature importanti
    if (type === 'success' || type === 'error') {
      size = 'large';
    }

    setNotification({
      show: true,
      message,
      type,
      size
    });
  }, []);

  // Funzione per chiudere notifiche
  const closeNotification = useCallback(() => {
    setNotification(prev => ({ ...prev, show: false }));
  }, []);

  // Inizializza le camere disponibili
  const initializeCameras = useCallback(async () => {
    try {
      console.log('ResponsabileTimekeeping: Inizializzazione camere...');
      
      if (!Html5Qrcode.getCameras) {
        throw new Error("Html5Qrcode.getCameras non disponibile");
      }
      
      const devices = await Html5Qrcode.getCameras();
      console.log('ResponsabileTimekeeping: Camere trovate:', devices);
      
      if (devices && devices.length > 0) {
        setCameras(devices);
        setSelectedCamera(devices[0].id);
        console.log('ResponsabileTimekeeping: Camera selezionata:', devices[0]);
      } else {
        console.warn('ResponsabileTimekeeping: Nessuna camera trovata');
        showNotification(
          '📷 Nessuna camera rilevata sul dispositivo\n⚙️ Verifica i permessi e ricarica la pagina',
          'error',
          'large'
        );
      }
    } catch (error) {
      console.error('ResponsabileTimekeeping: Errore accesso camera:', error);
      
      let errorMessage = '🚫 Errore accesso alla camera\n';
      if (error.message) {
        errorMessage += `🔍 ${error.message}\n`;
      }
      errorMessage += '🔄 Verifica i permessi e riprova';
      
      showNotification(errorMessage, 'error', 'large');
    }
  }, [showNotification]);

  // Inizializza al montaggio del componente
  useEffect(() => {
    initializeCameras();
    
    return () => {
      stopScanner();
    };
  }, [initializeCameras]);

  // Ferma lo scanner e pulisce le risorse
  const stopScanner = useCallback(async () => {
    try {
      console.log('ResponsabileTimekeeping: Fermando scanner...');
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
        console.log('ResponsabileTimekeeping: Timeout cleared');
      }

      if (html5QrCode.current) {
        try {
          const state = html5QrCode.current.getState();
          console.log('ResponsabileTimekeeping: Stato scanner:', state);
          
          if (state === Html5Qrcode.SCANNING || state === Html5Qrcode.PAUSED) {
            await html5QrCode.current.stop();
            console.log('ResponsabileTimekeeping: Scanner fermato con successo');
          }
        } catch (stopError) {
          console.warn('ResponsabileTimekeeping: Errore stop scanner (non critico):', stopError);
        }
        
        html5QrCode.current = null;
        console.log('ResponsabileTimekeeping: Istanza scanner pulita');
      }

      setIsScanning(false);
      setIsCaptured(false);
      console.log('ResponsabileTimekeeping: Stati resettati');
      
    } catch (error) {
      console.error('ResponsabileTimekeeping: Errore stop scanner:', error);
      html5QrCode.current = null;
      setIsScanning(false);
      setIsCaptured(false);
    }
  }, []);

  // ✅ NUOVA FUNZIONE: Processa timbratura dopo selezione posizione
  const processTimbratura = useCallback(async (userId, type, userName, locationId = null) => {
    try {
      const typeText = type === 'in' ? 'INGRESSO' : 'USCITA';

      // Mostra notifica di elaborazione
      showNotification(
        `⏳ Elaborazione ${typeText} tramite responsabile...`,
        'info',
        'normal'
      );

      // Processa la timbratura
      let result;
      if (type === 'in') {
        // ✅ Passa locationId a clockIn
        result = await timekeepingService.clockIn(userId, locationId, {
          deviceId,
          isAdmin,
          deviceType: 'responsabile',
          kioskMode: false,
          responsableAction: true
        });
        
        // ✅ Ottieni nome posizione per il messaggio
        const locationName = locationId ? await getLocationName(locationId) : null;
        
        // Notifica di successo dettagliata per INGRESSO
        let successMessage = `✅ TIMBRATURA RESPONSABILE\n` +
                              `👤 ${userName || result.userName || 'Dipendente'}\n` +
                              `🔵 INGRESSO registrato alle ${result.clockInTime}\n` +
                              `📅 Data: ${result.date}`;
        
        if (locationName) {
          successMessage += `\n📍 Posizione: ${locationName}`;
        }
        
        successMessage += `\n👨‍💼 Timbrato da: Responsabile`;
        
        showNotification(successMessage, 'success', 'large');
        
      } else {
        result = await timekeepingService.clockOut(userId, {
          deviceId,
          isAdmin,
          deviceType: 'responsabile',
          kioskMode: false,
          responsableAction: true
        });

        // Notifica di successo dettagliata per USCITA
        let successMessage = `✅ TIMBRATURA RESPONSABILE\n` +
          `👤 ${userName || result.userName || 'Dipendente'}\n` +
          `🔴 USCITA registrata alle ${result.clockOutTime}\n` +
          `⏱️ Ore totali: ${result.totalHours}h`;
        
        if (result.standardHours !== undefined && result.overtimeHours !== undefined) {
          successMessage += `\n📊 Standard: ${result.standardHours}h | Straordinario: ${result.overtimeHours}h`;
        }
        
        if (result.isAutoClose) {
          successMessage += `\n⚠️ Turno auto-chiuso per durata eccessiva`;
        }
        
        successMessage += `\n👨‍💼 Timbrato da: Responsabile`;
        
        showNotification(successMessage, 'success', 'large');
      }

      // Reset per nuova scansione
      setScanType('');

    } catch (error) {
      console.error('ResponsabileTimekeeping: Errore processamento timbratura:', error);
      
      // Gestisci errori specifici con messaggi chiari per responsabili
      let errorMessage = '❌ ERRORE TIMBRATURA RESPONSABILE\n';
      
      if (error.message.includes('già un ingresso attivo')) {
        errorMessage += '🔵 INGRESSO già registrato per questo dipendente\n' +
                       '⚠ Il dipendente deve prima timbrare l\'USCITA\n' +
                       '👨‍💼 Verifica lo stato attuale delle timbrature';
      } else if (error.message.includes('Nessun ingresso attivo')) {
        errorMessage += '🔴 USCITA non possibile\n' +
                       '⚠ Il dipendente deve prima timbrare l\'INGRESSO\n' +
                       '👨‍💼 Verifica lo storico delle timbrature';
      } else if (error.message.includes('QR code disattivato')) {
        errorMessage += '🚫 QR Code del dipendente disattivato\n' +
                       '👨‍💼 Contatta l\'amministratore per riattivarlo';
      } else if (error.message.includes('Utente non trovato')) {
        errorMessage += '👤 Dipendente non trovato nel sistema\n' +
                       '👨‍💼 Verifica che il QR Code sia valido';
      } else if (error.message.includes('non valido')) {
        errorMessage += '📱 QR Code non valido\n🔄 Riprova con un QR Code corretto';
      } else if (error.message.includes('Turno troppo lungo')) {
        errorMessage += '⏰ Turno troppo lungo rilevato\n✅ Auto-chiuso a 8 ore standard';
      } else {
        errorMessage += `📝 ${error.message}\n🔄 Riprova o contatta l\'assistenza`;
      }

      showNotification(errorMessage, 'error', 'large');
      
      // Reset per nuova scansione
      setScanType('');
    }
  }, [deviceId, isAdmin, showNotification]);

  // ✅ MODIFICATA: Gestisce conferma posizione dal modal
  const handleLocationConfirm = useCallback(async (locationId) => {
    console.log('ResponsabileTimekeeping: Posizione selezionata:', locationId);
    
    setShowLocationModal(false);
    
    if (pendingTimbratura) {
      const { userId, type, userName } = pendingTimbratura;
      
      // Processa la timbratura con la posizione selezionata
      await processTimbratura(userId, type, userName, locationId);
      
      setPendingTimbratura(null);
    }
  }, [pendingTimbratura, processTimbratura]);

  // ✅ MODIFICATA: Processa il QR Code scansionato
  const handleScan = useCallback(async (qrText, type) => {
    try {
      console.log('ResponsabileTimekeeping: QR scansionato:', qrText, 'Tipo:', type);
      
      // Verifica formato QR Code
      const parts = qrText.split(':');
      if (parts.length < 3 || parts[0] !== 'iacuzzo' || parts[1] !== 'user') {
        throw new Error('QR Code non valido - Formato non riconosciuto');
      }

      const userId = parts[2];
      
      // ✅ Se è un INGRESSO, mostra modal per selezione posizione
      if (type === 'in') {
        console.log('ResponsabileTimekeeping: Ingresso rilevato, mostro modal posizione');
        
        // Salva dati timbratura in sospeso
        setPendingTimbratura({
          userId,
          type,
          userName: null // Verrà recuperato dopo
        });
        
        // Mostra modal
        setShowLocationModal(true);
      } else {
        // ✅ Se è un'USCITA, processa direttamente (senza posizione)
        console.log('ResponsabileTimekeeping: Uscita rilevata, processa direttamente');
        await processTimbratura(userId, type, null, null);
      }

    } catch (error) {
      console.error('ResponsabileTimekeeping: Errore processamento QR:', error);
      
      let errorMessage = '❌ ERRORE SCANSIONE QR\n';
      if (error.message.includes('non valido')) {
        errorMessage += '📱 QR Code non valido\n🔄 Riprova con un QR Code corretto';
      } else {
        errorMessage += `📝 ${error.message}\n🔄 Riprova`;
      }
      
      showNotification(errorMessage, 'error', 'large');
      setScanType('');
    }
  }, [processTimbratura, showNotification]);

  // Avvia lo scanner
  const startScanner = useCallback(async (type) => {
    if (!selectedCamera) {
      showNotification(
        '📷 Seleziona una camera prima di iniziare\n⚙️ Verifica le impostazioni',
        'error'
      );
      return;
    }

    try {
      console.log(`ResponsabileTimekeeping: Avvio scanner ${type}...`);
      
      setIsScanning(true);
      setScanType(type);
      setIsCaptured(false);
      
      const typeText = type === 'in' ? 'INGRESSO' : 'USCITA';
      
      showNotification(
        `📸 Scanner ${typeText} responsabile attivato\n⏳ Inquadra il QR Code del dipendente`,
        'info',
        'normal'
      );

      // Inizializzazione asincrona
      setTimeout(async () => {
        try {
          console.log('ResponsabileTimekeeping: Timeout inizializzazione completato');
          
          // Crea nuova istanza solo se non esiste
          if (html5QrCode.current === null) {
            console.log('ResponsabileTimekeeping: Creazione nuova istanza Html5Qrcode');
            html5QrCode.current = new Html5Qrcode("qr-reader");
          }

          const config = {
            fps: 10,
            qrbox: { width: 300, height: 300 },
            aspectRatio: 1.0
          };

          let localCaptured = false;

          const onScanSuccess = async (decodedText, decodedResult) => {
            if (localCaptured) {
              console.log('ResponsabileTimekeeping: Scansione già processata, skip');
              return;
            }

            localCaptured = true;
            setIsCaptured(true);

            console.log('ResponsabileTimekeeping: QR Code rilevato:', decodedText);

            try {
              await stopScanner();
              
              // ✅ Usa la nuova funzione handleScan
              await handleScan(decodedText, type);
              
            } catch (processError) {
              console.error('ResponsabileTimekeeping: Errore processamento:', processError);
              localCaptured = false;
              setIsCaptured(false);
              setScanType('');
              setIsScanning(false);
            }
          };

          const onScanFailure = (error) => {
            // Non loggare errori continui di non rilevamento
            console.debug('ResponsabileTimekeeping: Scansione in corso...');
          };

          console.log('ResponsabileTimekeeping: Avvio scanner con camera:', selectedCamera);

          // Avvia scanner
          try {
            await html5QrCode.current.start(
              selectedCamera,
              config,
              onScanSuccess,
              onScanFailure
            );
            
            console.log(`ResponsabileTimekeeping: Scanner ${typeText} avviato con successo`);
          } catch (startError) {
            console.error('ResponsabileTimekeeping: Errore start scanner:', startError);
            throw new Error(`Errore avvio scanner: ${startError.message || 'Errore sconosciuto'}`);
          }

          // Timeout automatico dopo 30 secondi
          timeoutRef.current = setTimeout(async () => {
            console.log('ResponsabileTimekeeping: Timeout scanner raggiunto');
            if (!localCaptured) {
              await stopScanner();
              setScanType('');
              showNotification(
                '⏱️ Timeout scanner raggiunto\n🔄 Seleziona nuovamente il tipo di timbratura',
                'warning'
              );
            }
          }, 30000);

        } catch (initError) {
          console.error('ResponsabileTimekeeping: Errore inizializzazione scanner:', initError);
          await stopScanner();
          setScanType('');
          showNotification(
            `🚫 Errore inizializzazione scanner\n🔍 ${initError.message}\n🔄 Riprova`,
            'error'
          );
        }
      }, 100);

    } catch (error) {
      console.error('ResponsabileTimekeeping: Errore completo avvio scanner:', error);
      console.error('ResponsabileTimekeeping: Stack trace:', error.stack);
      
      // Pulizia in caso di errore
      setIsScanning(false);
      setScanType('');
      
      let errorMessage = '🚫 Errore avvio scanner responsabile\n';
      if (error.message) {
        errorMessage += `🔍 ${error.message}\n`;
      }
      errorMessage += '🔄 Riprova o ricarica la pagina';
      
      showNotification(errorMessage, 'error', 'large');
    }
  }, [selectedCamera, handleScan, stopScanner, showNotification]);

  // Gestisce la selezione del tipo di scansione
  const handleScanTypeSelection = (type) => {
    if (isScanning) {
      stopScanner();
    }
    startScanner(type);
  };

  // Gestisce stop manuale
  const handleStopScanner = async () => {
    await stopScanner();
    showNotification(
      'ℹ️ Scanner fermato dal responsabile\n🔄 Seleziona nuovamente il tipo di timbratura',
      'info'
    );
  };

  // Gestisce cambio camera
  const handleCameraChange = async (event) => {
    const newCameraId = event.target.value;
    setSelectedCamera(newCameraId);
    
    // Se sta scansionando, riavvia con la nuova camera
    if (isScanning && scanType) {
      await stopScanner();
      setTimeout(() => {
        startScanner(scanType);
      }, 500);
    }
  };

  return (
    <div className="timekeeping-scanner admin-mode">
      {/* Header */}
      <div className="scanner-header">
        <h2>Scanner Timbrature Responsabile</h2>
      </div>

      {/* Notifiche Migliorate */}
      <Notification
        message={notification.message}
        isVisible={notification.show}
        onClose={closeNotification}
        type={notification.type}
        size={notification.size}
        duration={notification.type === 'success' || notification.type === 'error' ? 8000 : 6000}
        playSound={true}
        enableVibration={true}
      />

      {/* ✅ NUOVO: Modal Selezione Posizione */}
      <LocationSelectionModal
        isOpen={showLocationModal}
        onClose={() => {
          setShowLocationModal(false);
          setPendingTimbratura(null);
          setScanType('');
        }}
        onConfirm={handleLocationConfirm}
        userName={pendingTimbratura?.userName || 'Dipendente'}
        scanType="in"
      />

      {/* Selettore Camera */}
      {cameras.length > 0 && (
        <div className="camera-selector">
          <label htmlFor="camera-select">Seleziona Camera:</label>
          <select
            id="camera-select"
            value={selectedCamera}
            onChange={handleCameraChange}
            disabled={isScanning}
          >
            {cameras.map((camera, index) => (
              <option key={camera.id} value={camera.id}>
                {camera.label || `Camera ${index + 1}`}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Pulsanti Selezione Tipo - Solo se non sta scansionando */}
      {!isScanning && cameras.length > 0 && (
        <div className="scan-type-buttons">
          <button
            className="scan-type-btn ingresso"
            onClick={() => handleScanTypeSelection('in')}
          >
            <div className="btn-icon">🔵</div>
            <div className="btn-text">INGRESSO</div>
          </button>
          <button
            className="scan-type-btn uscita"
            onClick={() => handleScanTypeSelection('out')}
          >
            <div className="btn-icon">🔴</div>
            <div className="btn-text">USCITA</div>
          </button>
        </div>
      )}

      {/* Container Scanner Attivo */}
      {isScanning && (
        <div className="scanner-container">
          <div className="scanner-status">
            <p>Scanner {scanType === 'in' ? 'INGRESSO' : 'USCITA'} Responsabile attivo</p>
            <button className="stop-scanner-btn" onClick={handleStopScanner}>
              Ferma Scanner
            </button>
          </div>
          <div id="qr-reader"></div>
        </div>
      )}

      {/* Messaggio se nessuna camera */}
      {cameras.length === 0 && (
        <div className="no-camera-message">
          <p>❌ Nessuna camera rilevata</p>
          <p>Verifica i permessi della camera e ricarica la pagina</p>
          <button onClick={initializeCameras}>
            Rileva Camera
          </button>
        </div>
      )}

      {/* Istruzioni per Responsabili */}
      {!isScanning && cameras.length > 0 && (
        <div style={{
          marginTop: '30px',
          padding: '20px',
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          borderRadius: '12px',
          textAlign: 'center',
          border: '2px solid #e67e22'
        }}>
          <h3 style={{ margin: '0 0 15px 0', color: '#e67e22' }}>
            👨‍💼 Scanner per Responsabili
          </h3>
          <p style={{ margin: '8px 0', color: '#6c757d', fontWeight: '500' }}>
            📸 Seleziona il tipo di timbratura (Ingresso o Uscita)
          </p>
          <p style={{ margin: '8px 0', color: '#6c757d', fontWeight: '500' }}>
            📍 Per gli ingressi, scegli la posizione dove timbra il dipendente
          </p>
          <p style={{ margin: '8px 0', color: '#6c757d', fontWeight: '500' }}>
            📸 Inquadra il QR code del dipendente da timbrare
          </p>
          <p style={{ margin: '8px 0', color: '#6c757d', fontWeight: '500' }}>
            📸 Verifica la conferma della registrazione
          </p>
          <div style={{ 
            margin: '20px 0 0 0', 
            padding: '15px',
            fontSize: '14px', 
            fontStyle: 'italic',
            color: '#d63384',
            background: 'rgba(214, 51, 132, 0.1)',
            borderRadius: '8px',
            border: '1px solid rgba(214, 51, 132, 0.2)'
          }}>
            ⚠️ <strong>Importante:</strong> Le timbrature effettuate tramite responsabile vengono 
            tracciate e registrate nel sistema per controlli amministrativi.
            <br />
            📊 Utilizzare solo per supporto ai dipendenti o correzioni autorizzate.
          </div>
        </div>
      )}
    </div>
  );
};

export default ResponsabileTimekeeping;